import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Facebook, 
  Instagram, 
  Image as ImageIcon, 
  Smile, 
  Clock, 
  Send, 
  Save,
  X,
  UploadCloud,
  ChevronDown,
  Calendar as CalendarIcon,
  MessageCircle,
  Heart,
  MoreHorizontal,
  ArrowLeft,
  Link as LinkIcon,
  Activity,
  AlertCircle,
  LayoutTemplate,
  PenSquare
} from 'lucide-react';
import api from '../../../services/api';

export default function SocialMediaComposer() {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [postStatus, setPostStatus] = useState(''); // 'draft', 'scheduled', 'published'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMediaConfirmModal, setShowMediaConfirmModal] = useState(false);
  const [showStartPrompt, setShowStartPrompt] = useState(false);
  const [pendingMediaAction, setPendingMediaAction] = useState(null);
  const [confirmModalLoading, setConfirmModalLoading] = useState(false);
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [alertConfig, setAlertConfig] = useState(null); // { title, message, type: 'warning' | 'error' | 'info' }
  const [content, setContent] = useState('');
  const [platforms, setPlatforms] = useState({ facebook: true, instagram: false });
  const [mediaUrls, setMediaUrls] = useState([]);
  const [deleteTargetPlatform, setDeleteTargetPlatform] = useState('all'); // 'all', 'facebook', 'instagram'
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // Mock Connected accounts info
  const [connectedPlatforms, setConnectedPlatforms] = useState({ facebook: true, instagram: true });
  
  const EMOJI_LIST = ['😀','😂','🥰','😎','🔥','✨','🍔','🍕','🥂','🍷','🥗','🎉','👏','🙌','👇','👉'];

  const convertLocalToUTC = (dateStr, timeStr, tzName) => {
    if (!dateStr || !timeStr) return null;
    try {
      const localDateTime = `${dateStr}T${timeStr}:00`;
      const date = new Date(localDateTime);
      // Determine offset difference for target timezone
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tzName,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false
      });
      
      const tzString = formatter.format(date);
      // Parse formatter parts to get offset diff
      const parts = formatter.formatToParts(date);
      const year = parseInt(parts.find(p => p.type === 'year').value);
      const month = parseInt(parts.find(p => p.type === 'month').value);
      const day = parseInt(parts.find(p => p.type === 'day').value);
      const hour = parseInt(parts.find(p => p.type === 'hour').value);
      const minute = parseInt(parts.find(p => p.type === 'minute').value);
      
      const targetUtc = Date.UTC(year, month - 1, day, hour, minute);
      const diff = targetUtc - date.getTime();
      return new Date(date.getTime() - diff).toISOString();
    } catch (e) {
      console.warn('Fallback to standard ISO string construction', e);
      return new Date(`${dateStr}T${timeStr}:00Z`).toISOString();
    }
  };

  const formatDateInTimezone = (utcString, tzName) => {
    if (!utcString) return { date: '', time: '' };
    try {
      const date = new Date(utcString);
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tzName,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const parts = formatter.formatToParts(date);
      const y = parts.find(p => p.type === 'year').value;
      const m = parts.find(p => p.type === 'month').value;
      const d = parts.find(p => p.type === 'day').value;
      const hh = parts.find(p => p.type === 'hour').value;
      const mm = parts.find(p => p.type === 'minute').value;
      return {
        date: `${y}-${m}-${d}`,
        time: `${hh}:${mm}`
      };
    } catch (e) {
      const dateObj = new Date(utcString);
      return {
        date: dateObj.toISOString().split('T')[0],
        time: dateObj.toTimeString().split(' ')[0].substring(0, 5)
      };
    }
  };

  useEffect(() => {
    const initComposer = async () => {
      try {
        const [accountsRes, campaignsRes, templatesRes] = await Promise.all([
          api.get('/api/social-media/accounts'),
          api.get('/api/campaigns').catch(() => ({ data: [] })),
          api.get('/api/social-media/templates').catch(() => ({ data: [] }))
        ]);
        
        const accountsData = accountsRes.data || [];
        setConnectedPlatforms({
          facebook: accountsData.some(a => a.platform === 'facebook' && a.is_active),
          instagram: accountsData.some(a => a.platform === 'instagram' && a.is_active)
        });
        
        // Auto-select all connected platforms by default
        setPlatforms({
          facebook: accountsData.some(a => a.platform === 'facebook' && a.is_active),
          instagram: accountsData.some(a => a.platform === 'instagram' && a.is_active)
        });

        setCampaigns(campaignsRes.data || []);
        setTemplates(templatesRes.data || []);

        // Load existing post if edit parameter is present
        const editId = new URLSearchParams(location.search).get('edit');
        if (editId) {
          const postRes = await api.get(`/api/social-media/posts/${editId}`);
          const postData = postRes.data;
          if (postData) {
            setContent(postData.content || '');
            setMediaUrls(postData.media_urls || []);
            setPostStatus(postData.status || '');
            setPlatforms({
              facebook: postData.platforms.includes('facebook'),
              instagram: postData.platforms.includes('instagram')
            });
            if (postData.campaign_id) {
              setSelectedCampaign(postData.campaign_id.toString());
            }
            const savedTz = postData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            setTimezone(savedTz);
            if (postData.scheduled_at) {
              setIsScheduling(true);
              const { date, time } = formatDateInTimezone(postData.scheduled_at, savedTz);
              setScheduleDate(date);
              setScheduleTime(time);
            }

            // Auto-sync from Facebook on page load for published posts
            if (postData.status === 'published' && postData.platform_post_ids?.facebook) {
              try {
                const syncRes = await api.post(`/api/social-media/posts/${editId}/sync-from-facebook`);
                if (syncRes.data?.content && syncRes.data.content !== postData.content) {
                  setContent(syncRes.data.content);
                }
              } catch (syncErr) {
                console.warn('Auto-sync from Facebook skipped:', syncErr.message);
              }
            }
          }
        } else {
          const hasTemplates = (templatesRes.data || []).length > 0;
          if (hasTemplates) {
            setShowStartPrompt(true);
          }
        }
      } catch (error) {
        console.error('Composer loading error:', error);
      }
    };
    initComposer();
  }, [location.search]);

  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaTypes, setMediaTypes] = useState([]); // 'image' | 'video' per slot

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const proceedUpload = async () => {
      const localPreviews = files.map(file => ({
        file,
        localUrl: URL.createObjectURL(file),
        isVideo: file.type.startsWith('video/')
      }));

      // Show local blob previews immediately
      setMediaUrls(prev => [...prev, ...localPreviews.map(p => p.localUrl)].slice(0, 4));
      setMediaTypes(prev => [...prev, ...localPreviews.map(p => p.isVideo ? 'video' : 'image')].slice(0, 4));

      try {
        setUploadingMedia(true);
        const uploadedItems = [];

        for (const item of localPreviews) {
          const formData = new FormData();
          formData.append('file', item.file); // new /api/upload/media endpoint uses 'file'

          const response = await api.post('/api/upload/media', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          if (response.data?.secureUrl) {
            uploadedItems.push({
              url: response.data.secureUrl,
              type: response.data.resourceType || (item.isVideo ? 'video' : 'image')
            });
          }
        }

        // Replace blob URLs with real Cloudinary URLs
        setMediaUrls(prev => {
          const kept = prev.filter(url => !url.startsWith('blob:'));
          return [...kept, ...uploadedItems.map(i => i.url)].slice(0, 4);
        });
        setMediaTypes(prev => {
          const kept = prev.slice(0, prev.filter((_, idx) => !mediaUrls[idx]?.startsWith('blob:')).length);
          return [...kept, ...uploadedItems.map(i => i.type)].slice(0, 4);
        });
      } catch (error) {
        console.error('Media upload error:', error);
        setMediaUrls(prev => prev.filter(url => !url.startsWith('blob:')));
        setMediaTypes(prev => prev.slice(0, prev.length - localPreviews.length));
        setAlertConfig({
          title: 'Upload Failed',
          message: error.response?.data?.message || 'Failed to upload media. Please try again.',
          type: 'error'
        });
      } finally {
        setUploadingMedia(false);
      }
    };

    if (postStatus === 'published') {
      setPendingMediaAction(() => proceedUpload);
      setShowMediaConfirmModal(true);
    } else {
      await proceedUpload();
    }
  };

  const removeMedia = (index) => {
    const proceedRemove = () => {
      setMediaUrls(prev => prev.filter((_, i) => i !== index));
      setMediaTypes(prev => prev.filter((_, i) => i !== index));
    };

    if (postStatus === 'published') {
      setPendingMediaAction(() => proceedRemove);
      setShowMediaConfirmModal(true);
    } else {
      proceedRemove();
    }
  };

  const togglePlatform = (platform) => {
    setPlatforms(prev => ({ ...prev, [platform]: !prev[platform] }));
  };

  const insertEmoji = (emoji) => {
    setContent(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleSelectTemplate = (templateId) => {
    if (!templateId) return;
    const selected = templates.find(t => t.id === templateId);
    if (selected) {
      setContent(selected.content || '');
      if (selected.media_urls) {
        setMediaUrls(selected.media_urls);
      }
      if (selected.platforms) {
        setPlatforms({
          facebook: selected.platforms.includes('facebook') && connectedPlatforms.facebook,
          instagram: selected.platforms.includes('instagram') && connectedPlatforms.instagram
        });
      }
    }
  };

  const handlePublish = async (publishStatus) => {
    if (!content.trim() && mediaUrls.length === 0) {
      setAlertConfig({
        title: 'Empty Post',
        message: 'Post must contain text or an image.',
        type: 'warning'
      });
      return;
    }
    const activePlatforms = Object.keys(platforms).filter(p => platforms[p]);
    if (activePlatforms.length === 0) {
      setAlertConfig({
        title: 'No Platform Selected',
        message: 'Select at least one platform.',
        type: 'warning'
      });
      return;
    }

    // Block publish if image is still being uploaded to Cloudinary
    if (uploadingMedia) {
      setAlertConfig({
        title: 'Upload In Progress',
        message: 'Please wait for the media to finish uploading before publishing.',
        type: 'warning'
      });
      return;
    }

    // Safety: filter out any blob:// URLs — these are local-only and cannot be sent to Facebook/Instagram
    const safeMediaUrls = mediaUrls.filter(url => !url.startsWith('blob:'));
    if (safeMediaUrls.length < mediaUrls.length) {
      setAlertConfig({
        title: 'Media Upload Failed',
        message: 'One or more media files failed to upload. Please remove them and re-add before publishing.',
        type: 'error'
      });
      return;
    }

    const hasVideo = mediaTypes.some(t => t === 'video') ||
      safeMediaUrls.some(u => /\.(mp4|mov|webm|avi|mkv)$/i.test(u));

    try {
      setLoading(true);
      const isEdit = new URLSearchParams(location.search).get('edit');
      const payload = {
        content,
        media_urls: safeMediaUrls,
        media_types: mediaTypes.filter((_, idx) => !mediaUrls[idx]?.startsWith('blob:')).slice(0, safeMediaUrls.length),
        platforms: activePlatforms,
        status: postStatus === 'published' ? 'published' : (publishStatus === 'published' ? 'publish_now' : publishStatus),
        scheduled_at: isScheduling ? convertLocalToUTC(scheduleDate, scheduleTime, timezone) : null,
        timezone: isScheduling ? timezone : null,
        campaign_id: selectedCampaign ? parseInt(selectedCampaign) : null
      };
      
      let res;
      if (isEdit) {
        res = await api.put(`/api/social-media/posts/${isEdit}`, payload);
      } else {
        res = await api.post('/api/social-media/posts', payload);
      }
      
      setLoading(false);

      // 202 = backend accepted publish job, running in background (happens for videos)
      if (res.status === 202 || res.data?._publishing) {
        const msg = hasVideo
          ? 'Your video is being processed and published. This may take up to 90 seconds. Check the dashboard for the final status.'
          : 'Your post is being published in the background. Check the dashboard for the final status.';
        setAlertConfig({
          title: '🚀 Publishing In Progress',
          message: msg,
          type: 'info',
          onClose: () => navigate('/admin/social-media')
        });
        return;
      }

      if (res.data?.sync_warning) {
        setAlertConfig({
          title: 'Facebook Sync Limitation',
          message: res.data.sync_warning,
          type: 'info',
          onClose: () => navigate('/admin/social-media')
        });
        return;
      }
      
      navigate('/admin/social-media');
    } catch (error) {
      console.error('Error creating/saving post:', error);
      setAlertConfig({
        title: 'Failed to Save Post',
        message: error.response?.data?.error || 'Failed to process post. Please try again.',
        type: 'error'
      });
      setLoading(false);
    }
  };


  const handleDelete = async () => {
    const editId = new URLSearchParams(location.search).get('edit');
    if (!editId) return;
    try {
      setLoading(true);
      const queryParam = deleteTargetPlatform !== 'all' ? `?platforms=${deleteTargetPlatform}` : '';
      await api.delete(`/api/social-media/posts/${editId}${queryParam}`);
      setLoading(false);
      setShowDeleteModal(false);
      navigate('/admin/social-media');
    } catch (error) {
      console.error('Error deleting post:', error);
      setAlertConfig({
        title: 'Delete Failed',
        message: error.response?.data?.error || 'Failed to delete post. Please try again.',
        type: 'error'
      });
      setLoading(false);
    }
  };

  const handleSyncFromFacebook = async () => {
    const editId = new URLSearchParams(location.search).get('edit');
    if (!editId) return;
    try {
      setLoading(true);
      const res = await api.post(`/api/social-media/posts/${editId}/sync-from-facebook`);
      setContent(res.data.content);
      setAlertConfig({
        title: 'Synced Successfully',
        message: 'The post content has been successfully updated with the latest live status from Facebook.',
        type: 'info'
      });
      setLoading(false);
    } catch (error) {
      console.error('Error syncing post from Facebook:', error);
      setAlertConfig({
        title: 'Sync Failed',
        message: error.response?.data?.error || 'Failed to sync content from Facebook. Please try again.',
        type: 'error'
      });
      setLoading(false);
    }
  };

  // Preview Components
  const FacebookPreview = () => {
    const isVideo = mediaTypes[0] === 'video' || (mediaUrls[0] && mediaUrls[0].match(/\.(mp4|mov|webm|avi|mkv)$/i));
    return (
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden w-full max-w-sm mx-auto animate-in fade-in">
        <div className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">R</div>
          <div>
            <h4 className="font-bold text-sm text-gray-900">Your Restaurant</h4>
            <p className="text-xs text-gray-500">Just now • 🌎</p>
          </div>
          <MoreHorizontal className="ml-auto text-gray-500" size={18} />
        </div>
        <div className="px-4 pb-3 text-sm text-gray-900 whitespace-pre-wrap">
          {content || "Your post text will appear here..."}
        </div>
        {mediaUrls.length > 0 && (
          <div className="grid gap-0.5 bg-black col-span-1">
            {isVideo ? (
              <video src={mediaUrls[0]} className="w-full object-cover h-64" controls muted playsInline />
            ) : (
              <img src={mediaUrls[0]} className="w-full object-cover h-64" alt="Post media" />
            )}
          </div>
        )}
        <div className="p-3 border-t border-gray-100 flex justify-between text-gray-500 text-sm font-medium">
          <button className="flex-1 py-1 flex items-center justify-center gap-2 hover:bg-gray-50 rounded-lg"><span className="text-blue-500">👍</span> Like</button>
          <button className="flex-1 py-1 flex items-center justify-center gap-2 hover:bg-gray-50 rounded-lg"><MessageCircle size={16} /> Comment</button>
          <button className="flex-1 py-1 flex items-center justify-center gap-2 hover:bg-gray-50 rounded-lg"><Send size={16} /> Share</button>
        </div>
      </div>
    );
  };

  const InstagramPreview = () => {
    const isVideo = mediaTypes[0] === 'video' || (mediaUrls[0] && mediaUrls[0].match(/\.(mp4|mov|webm|avi|mkv)$/i));
    return (
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden w-full max-w-sm mx-auto animate-in fade-in">
        <div className="p-3 flex items-center gap-3 border-b border-gray-50">
          <div className="w-8 h-8 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500 p-0.5 rounded-full">
            <div className="w-full h-full bg-white rounded-full flex items-center justify-center font-bold text-xs">R</div>
          </div>
          <h4 className="font-semibold text-sm text-gray-900">your_restaurant</h4>
          <MoreHorizontal className="ml-auto text-gray-900" size={18} />
        </div>
        
        {mediaUrls.length > 0 ? (
          <div className="bg-black">
            {isVideo ? (
              <video src={mediaUrls[0]} className="w-full h-80 object-cover" controls muted playsInline />
            ) : (
              <img src={mediaUrls[0]} className="w-full h-80 object-cover" alt="Post media" />
            )}
          </div>
        ) : (
          <div className="w-full h-80 bg-gray-100 flex flex-col items-center justify-center text-gray-400 gap-2">
            <ImageIcon size={32} />
            <span className="text-sm">Image or video required for Instagram</span>
          </div>
        )}

      
      <div className="p-3">
        <div className="flex gap-4 mb-2 text-gray-900">
          <Heart size={24} />
          <MessageCircle size={24} />
          <Send size={24} className="-mt-1" />
          <Save size={24} className="ml-auto" />
        </div>
        <p className="text-sm font-semibold mb-1">12 likes</p>
        <div className="text-sm">
          <span className="font-semibold mr-2">your_restaurant</span>
          <span className="whitespace-pre-wrap">{content || "Write a caption..."}</span>
        </div>
      </div>
    </div>
  );
};

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      {/* Top Navbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/admin/social-media')} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-bold text-gray-900">
            {postStatus === 'published' ? 'Edit Published Post' : new URLSearchParams(location.search).get('edit') ? 'Edit Post' : 'Create Post'}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {postStatus === 'published' ? (
            <>
              <button 
                onClick={() => setShowDeleteModal(true)}
                disabled={loading}
                className="px-4 py-2 text-sm font-semibold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              >
                Delete Post
              </button>
              <button 
                onClick={handleSyncFromFacebook}
                disabled={loading}
                className="px-4 py-2 text-sm font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded-lg hover:bg-blue-100/70 transition-colors flex items-center gap-1.5"
              >
                Sync from Facebook
              </button>
              <button 
                onClick={() => handlePublish('published')}
                disabled={loading || uploadingMedia}
                className={`px-5 py-2 text-sm font-semibold text-white rounded-lg transition-all flex items-center gap-2 ${
                  loading || uploadingMedia
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-lg hover:shadow-blue-500/30'
                }`}
              >
                {loading ? <Clock className="animate-spin" size={16} /> : <Save size={16} />}
                Save Changes
              </button>
            </>
          ) : (
            <>
              {new URLSearchParams(location.search).get('edit') && (
                <button 
                  onClick={() => setShowDeleteModal(true)}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-semibold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Delete Post
                </button>
              )}
              <button 
                onClick={() => handlePublish('draft')}
                disabled={loading || uploadingMedia}
                className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors ${
                  loading || uploadingMedia ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                }`}
              >
                Save Draft
              </button>
              <button 
                onClick={() => handlePublish(isScheduling ? 'scheduled' : 'published')}
                disabled={loading || uploadingMedia || (platforms.instagram && mediaUrls.length === 0)}
                className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-all flex items-center gap-2 ${
                  loading || uploadingMedia || (platforms.instagram && mediaUrls.length === 0) 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-lg hover:shadow-blue-500/30'
                }`}
              >
                {loading ? <Clock className="animate-spin" size={16} /> : isScheduling ? <CalendarIcon size={16} /> : <Send size={16} />}
                {isScheduling ? 'Schedule' : 'Publish Now'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left Column: Composer */}
        <div className="flex-1 overflow-y-auto p-6 lg:border-r border-gray-200">
          <div className="max-w-2xl mx-auto space-y-6 pb-20">

            
            {/* Platform Selection */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
              <label className="block text-sm font-bold text-gray-900 mb-3">Select Platforms</label>
              <div className="flex gap-3">
                <button 
                  onClick={() => connectedPlatforms.facebook && togglePlatform('facebook')}
                  className={`flex-1 p-3 rounded-xl border flex flex-col items-center justify-center gap-2 transition-all ${
                    !connectedPlatforms.facebook ? 'opacity-50 bg-gray-50 cursor-not-allowed' :
                    platforms.facebook ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm ring-1 ring-blue-500' : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                  }`}
                >
                  <Facebook size={24} className={platforms.facebook ? 'text-blue-600' : 'text-gray-400'} />
                  <span className="text-sm font-medium">Facebook</span>
                </button>
                <button 
                  onClick={() => connectedPlatforms.instagram && togglePlatform('instagram')}
                  className={`flex-1 p-3 rounded-xl border flex flex-col items-center justify-center gap-2 transition-all ${
                    !connectedPlatforms.instagram ? 'opacity-50 bg-gray-50 cursor-not-allowed' :
                    platforms.instagram ? 'border-pink-500 bg-pink-50 text-pink-700 shadow-sm ring-1 ring-pink-500' : 'border-gray-200 hover:border-pink-200 hover:bg-gray-50'
                  }`}
                >
                  <Instagram size={24} className={platforms.instagram ? 'text-pink-600' : 'text-gray-400'} />
                  <span className="text-sm font-medium">Instagram</span>
                </button>
              </div>
            </div>

            {/* Content Editor */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 group focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all relative">
              <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-gray-50 rounded-t-2xl">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Post Content</span>
                <div className="relative">
                  <button 
                    type="button" 
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)} 
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    <Smile size={18} />
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute right-0 mt-1 bg-white border border-gray-200 shadow-xl rounded-lg p-2 grid grid-cols-4 gap-1 z-30 animate-in fade-in slide-in-from-top-2 w-40">
                      {EMOJI_LIST.map(em => (
                        <button key={em} type="button" onClick={() => insertEmoji(em)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded text-xl">
                          {em}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="What do you want to share with your audience?"
                className="w-full h-40 p-4 resize-none focus:outline-none text-gray-900 rounded-b-2xl"
              />
              <div className="px-4 py-2 bg-white flex justify-between items-center text-xs text-gray-400 border-t border-gray-50 rounded-b-2xl">
                <span>{content.length} characters</span>
                <span className={content.length > 2200 && platforms.instagram ? 'text-red-500 font-medium' : ''}>
                  {platforms.instagram ? `${content.length} / 2200 (IG Limit)` : ''}
                </span>
              </div>
            </div>

            {/* Media Upload */}
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-bold text-gray-900">Media</label>
                <span className="text-xs text-gray-500">{mediaUrls.length}/4</span>
              </div>
              
              {platforms.instagram && mediaUrls.length === 0 && (
                <div className="mb-3 p-2 bg-orange-50 border border-orange-100 rounded text-xs text-orange-700 flex items-center gap-1.5">
                  <AlertCircle size={14} /> Instagram requires at least one image or video.
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                {mediaUrls.map((url, i) => {
                  const isVideo = mediaTypes[i] === 'video' || url.match(/\.(mp4|mov|webm|avi|mkv)$/i);
                  return (
                    <div key={i} className="relative aspect-square rounded-lg overflow-hidden group border border-gray-200 bg-black">
                      {isVideo ? (
                        <video src={url} className="w-full h-full object-cover" muted playsInline />
                      ) : (
                        <img src={url} className="w-full h-full object-cover" alt="upload preview" />
                      )}
                      {isVideo && (
                        <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-1">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
                          Video
                        </div>
                      )}
                      <button
                        onClick={() => removeMedia(i)}
                        className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
                {mediaUrls.length < 4 && (
                  <button 
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingMedia}
                    className={`aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 transition-colors flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-blue-600 ${
                      uploadingMedia ? 'opacity-70 cursor-wait' : ''
                    }`}
                  >
                    {uploadingMedia ? (
                      <>
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-xs font-medium">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <UploadCloud size={24} />
                        <span className="text-xs font-medium">Add Media</span>
                        <span className="text-xs text-gray-400">Image or Video</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*,video/*" 
                multiple
                onChange={handleMediaUpload}
              />
            </div>

            {/* Publishing Options */}
            {postStatus !== 'published' && (
              <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                <label className="block text-sm font-bold text-gray-900">Publishing Options</label>
                
                <div className="flex bg-gray-100 p-1 rounded-xl">
                  <button 
                    onClick={() => setIsScheduling(false)}
                    className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${!isScheduling ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    Post Now
                  </button>
                  <button 
                    onClick={() => setIsScheduling(true)}
                    className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${isScheduling ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    Schedule
                  </button>
                </div>

                {isScheduling && (
                  <div className="space-y-3 pt-2 animate-in fade-in slide-in-from-top-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                        <input 
                          type="date" 
                          value={scheduleDate}
                          onChange={(e) => setScheduleDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" 
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Time</label>
                        <input 
                          type="time" 
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" 
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Timezone</label>
                      <select 
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                      >
                        <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                        <option value="UTC">UTC / GMT</option>
                        <option value="America/New_York">America/New_York (EST/EDT)</option>
                        <option value="America/Chicago">America/Chicago (CST/CDT)</option>
                        <option value="America/Denver">America/Denver (MST/MDT)</option>
                        <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                        <option value="Europe/London">Europe/London (GMT/BST)</option>
                        <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                        <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                        <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                        <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="pt-2 border-t border-gray-100 mt-4">
                  <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                    <LayoutTemplate size={12} className="text-gray-400" /> Apply Template (Optional)
                  </label>
                  <div className="relative">
                    <select 
                      onChange={(e) => handleSelectTemplate(e.target.value)}
                      defaultValue=""
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none bg-white"
                    >
                      <option value="">Choose a template...</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.template_name || 'Unnamed Template'}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-2.5 text-gray-400 pointer-events-none" size={16} />
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-100 mt-4">
                  <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                    <LinkIcon size={12} /> Link to Campaign (Optional)
                  </label>
                  <div className="relative">
                    <select 
                      value={selectedCampaign}
                      onChange={(e) => setSelectedCampaign(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none bg-white"
                    >
                      <option value="">None</option>
                      {campaigns.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-2.5 text-gray-400 pointer-events-none" size={16} />
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Right Column: Live Preview */}
        <div className="w-full lg:w-[450px] bg-gray-100/80 p-6 overflow-y-auto hidden md:block">
          <div className="sticky top-0">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-6 flex items-center gap-2">
              <Activity size={16} className="text-blue-500" /> Live Preview
            </h3>
            
            <div className="space-y-8">
              {(!platforms.facebook && !platforms.instagram) && (
                <div className="text-center py-12 text-gray-400 flex flex-col items-center">
                  <ImageIcon size={48} className="mb-3 opacity-20" />
                  <p>Select a platform to preview your post</p>
                </div>
              )}

              {platforms.facebook && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-blue-600 font-medium text-sm ml-1">
                    <Facebook size={16} /> Facebook Preview
                  </div>
                  <FacebookPreview />
                </div>
              )}

              {platforms.instagram && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-pink-600 font-medium text-sm ml-1">
                    <Instagram size={16} /> Instagram Preview
                  </div>
                  <InstagramPreview />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Themed Confirm Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setShowDeleteModal(false)}
          ></div>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 p-6 relative z-10 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3.5 mb-4 text-red-600">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertCircle size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Delete Social Post?</h3>
                <p className="text-xs text-gray-500">This action cannot be undone.</p>
              </div>
            </div>

            {/* If the post was published on both Facebook and Instagram, show option selectors */}
            {(platforms.facebook && platforms.instagram) ? (
              <div className="mb-4 bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-3">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Select platform to delete:</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2.5 text-sm font-semibold text-gray-700 cursor-pointer">
                    <input 
                      type="radio" 
                      name="deleteTarget" 
                      value="all" 
                      checked={deleteTargetPlatform === 'all'} 
                      onChange={() => setDeleteTargetPlatform('all')}
                      className="text-red-600 focus:ring-red-500"
                    />
                    <span>Delete from Both (Full deletion)</span>
                  </label>
                  <label className="flex items-center gap-2.5 text-sm font-semibold text-gray-700 cursor-pointer">
                    <input 
                      type="radio" 
                      name="deleteTarget" 
                      value="facebook" 
                      checked={deleteTargetPlatform === 'facebook'} 
                      onChange={() => setDeleteTargetPlatform('facebook')}
                      className="text-red-600 focus:ring-red-500"
                    />
                    <span>Delete from Facebook Page only</span>
                  </label>
                  <label className="flex items-center gap-2.5 text-sm font-semibold text-gray-700 cursor-pointer">
                    <input 
                      type="radio" 
                      name="deleteTarget" 
                      value="instagram" 
                      checked={deleteTargetPlatform === 'instagram'} 
                      onChange={() => setDeleteTargetPlatform('instagram')}
                      className="text-red-600 focus:ring-red-500"
                    />
                    <span>Remove from Instagram dashboard only</span>
                  </label>
                </div>
              </div>
            ) : null}

            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              {deleteTargetPlatform === 'all' && (platforms.instagram) && (
                "Are you sure you want to delete this post? It will be removed from your dashboard, Facebook Page, and Instagram. Note: To delete from Instagram via API, the 'instagram_manage_contents' permission must be granted."
              )}
              {deleteTargetPlatform === 'all' && (!platforms.instagram) && (
                "Are you sure you want to delete this post? It will be deleted from your dashboard and immediately removed from Facebook Page."
              )}
              {deleteTargetPlatform === 'facebook' && (
                "Are you sure you want to delete this post from Facebook? It will be immediately removed from Facebook Page but will remain listed on your dashboard for Instagram."
              )}
              {deleteTargetPlatform === 'instagram' && (
                "Are you sure you want to delete this post from Instagram? It will remain live on your Facebook Page. Note: To delete from Instagram via API, the 'instagram_manage_contents' permission must be granted."
              )}
            </p>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={loading}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className={`px-5 py-2 text-sm font-semibold text-white rounded-xl transition-all flex items-center gap-2 ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-red-500 to-rose-600 hover:shadow-lg hover:shadow-rose-100 active:scale-95'}`}
              >
                {loading && <Clock className="animate-spin" size={14} />}
                {loading ? 'Deleting...' : 'Delete Post'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Change Confirmation Modal */}
      {showMediaConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => {
              setPendingMediaAction(null);
              setShowMediaConfirmModal(false);
            }}
          ></div>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 p-6 relative z-10 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3.5 mb-4 text-amber-600">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertCircle size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Modify Published Media?</h3>
                <p className="text-xs text-gray-500">Facebook API limitation warning</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              Meta does not support swapping or removing images from an already published post via API. 
              To apply this change, <strong>the existing Facebook post will be deleted</strong> and a <strong>new one will be published</strong> when you click <strong>Save Changes</strong>.
              <br/><br/>
              Do you want to proceed with editing the media?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setPendingMediaAction(null);
                  setShowMediaConfirmModal(false);
                }}
                disabled={confirmModalLoading}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-all disabled:opacity-50"
              >
                No, Keep it
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (pendingMediaAction) {
                    setConfirmModalLoading(true);
                    await pendingMediaAction();
                    setConfirmModalLoading(false);
                  }
                  setPendingMediaAction(null);
                  setShowMediaConfirmModal(false);
                }}
                disabled={confirmModalLoading}
                className={`px-5 py-2 text-sm font-semibold text-white rounded-xl transition-all flex items-center gap-2 ${
                  confirmModalLoading ? 'bg-amber-400 cursor-not-allowed' : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:shadow-lg active:scale-95'
                }`}
              >
                {confirmModalLoading && <Clock className="animate-spin" size={14} />}
                {confirmModalLoading ? 'Processing...' : 'Yes, Modify Media'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Start Creation Prompt Modal */}
      {showStartPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"></div>
          <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl border border-gray-100 p-8 relative z-10 animate-in zoom-in-95 duration-200">
            <div className="text-center mb-6">
              <h3 className="font-bold text-gray-900 text-2xl">Create New Post</h3>
              <p className="text-sm text-gray-500 mt-1">Choose how you want to start planning your post</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Option 1: Blank Post */}
              <button
                type="button"
                onClick={() => setShowStartPrompt(false)}
                className="flex flex-col items-center gap-3 p-6 border-2 border-gray-100 rounded-2xl hover:border-blue-500 hover:bg-blue-50/30 transition-all text-center group"
              >
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
                  <PenSquare size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm">Start Blank</h4>
                  <p className="text-xs text-gray-500 mt-1">Write your content from scratch</p>
                </div>
              </button>

              {/* Option 2: Use Template */}
              <div className="flex flex-col items-center gap-3 p-6 border-2 border-gray-100 rounded-2xl hover:border-purple-500 hover:bg-purple-50/30 transition-all text-center group relative">
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600 group-hover:scale-110 transition-transform">
                  <LayoutTemplate size={24} />
                </div>
                <div className="w-full">
                  <h4 className="font-bold text-gray-900 text-sm">Use Template</h4>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleSelectTemplate(e.target.value);
                        setShowStartPrompt(false);
                      }
                    }}
                    defaultValue=""
                    className="mt-2 w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs bg-white text-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
                  >
                    <option value="">Select a template...</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.template_name || 'Unnamed'}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setShowStartPrompt(false)}
                className="text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
              >
                Skip & start blank
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Themed Alert Modal */}
      {alertConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => {
              if (alertConfig.onClose) alertConfig.onClose();
              setAlertConfig(null);
            }}
          ></div>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 p-6 relative z-10 animate-in zoom-in-95 duration-200">
            <div className={`flex items-center gap-3.5 mb-4 ${
              alertConfig.type === 'error' ? 'text-red-600' :
              alertConfig.type === 'info' ? 'text-indigo-600' : 'text-amber-600'
            }`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                alertConfig.type === 'error' ? 'bg-red-50' :
                alertConfig.type === 'info' ? 'bg-indigo-50' : 'bg-amber-50'
              }`}>
                <AlertCircle size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">{alertConfig.title}</h3>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              {alertConfig.message}
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  if (alertConfig.onClose) alertConfig.onClose();
                  setAlertConfig(null);
                }}
                className={`px-5 py-2 text-sm font-semibold text-white rounded-xl active:scale-95 transition-all shadow-sm ${
                  alertConfig.type === 'error' ? 'bg-gradient-to-r from-red-500 to-rose-600' :
                  alertConfig.type === 'info' ? 'bg-gradient-to-r from-blue-600 to-indigo-600' :
                  'bg-gradient-to-r from-amber-500 to-orange-600'
                }`}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
